/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  ErrorSolidIcon,
  HostIcon,
  WebBrowserIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";

import type { TFunction } from "i18next";
import { ErrorView } from "../ErrorView";
import { FullScreenView } from "../FullScreenView";
import type { TerminalCallError } from "../recovery/CallRecoveryTypes";
import type { WidgetHelpers } from "../widget";

interface Props {
  readonly error: TerminalCallError;
  readonly widget: WidgetHelpers | null;
}

/**
 * Operational terminal outcome одной пользовательской call session.
 *
 * Экран намеренно не предлагает reconnect: новый Join должен начинаться только
 * как новая пользовательская session после явного выхода с этого экрана.
 */
export const CallTerminalView: FC<Props> = ({
  error,
  widget,
}): ReactElement => {
  const { t } = useTranslation();
  const view = getTerminalView(error.code, t);

  return (
    <FullScreenView>
      <ErrorView Icon={view.icon} title={view.title} widget={widget}>
        <p>{view.description}</p>
      </ErrorView>
    </FullScreenView>
  );
};

function getTerminalView(
  code: TerminalCallError["code"],
  t: TFunction,
): {
  readonly title: string;
  readonly description: string;
  readonly icon: typeof ErrorSolidIcon;
} {
  switch (code) {
    case "auth_revoked":
    case "access_forbidden":
    case "permission_denied":
      return {
        title: t("call_terminal.access.title"),
        description: t("call_terminal.access.description"),
        icon: ErrorSolidIcon,
      };
    case "room_unavailable":
      return {
        title: t("call_terminal.room.title"),
        description: t("call_terminal.room.description"),
        icon: ErrorSolidIcon,
      };
    case "unsupported_webrtc":
    case "irrecoverable_e2ee":
      return {
        title: t("call_terminal.browser.title"),
        description: t("call_terminal.browser.description"),
        icon: WebBrowserIcon,
      };
    case "incompatible_configuration":
      return {
        title: t("call_terminal.configuration.title"),
        description: t("call_terminal.configuration.description"),
        icon: HostIcon,
      };
    case "terminal_server_error":
      return {
        title: t("call_terminal.server.title"),
        description: t("call_terminal.server.description"),
        icon: HostIcon,
      };
    case "repeated_unknown_failure":
      return {
        title: t("call_terminal.unexpected.title"),
        description: t("call_terminal.unexpected.description"),
        icon: ErrorSolidIcon,
      };
  }
}
